import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiClientError, apiRequest } from '../api/client.js';
import { readCollection } from '../api/collections.js';
import { useSession } from '../auth/session.js';
import { useBusinessAccounts } from '../business/scope.js';
import { useMetaIntegration } from '../system/status.js';
import { CampaignComposer } from '../campaigns/composer.js';
import { CampaignConfirmation, type PendingAction } from '../campaigns/confirmation.js';
import { stateLabel, validValidation, validationMessages, type Campaign, type Validation } from '../campaigns/model.js';

const failureMessage = (cause: unknown): string => cause instanceof ApiClientError && [401, 403].includes(cause.status)
  ? 'Sua sessão não permite esta alteração. Entre novamente para obter um CSRF válido.'
  : cause instanceof ApiClientError && cause.status === 503
    ? 'A integração Meta não está configurada. Operação bloqueada.'
    : 'A API não confirmou a operação. Atualize a lista e consulte a validação antes de tentar novamente.';

export const CampaignsRoute = () => {
  const { account, accounts, error: accountError } = useBusinessAccounts();
  const accountId = account?.id ?? null;
  const { csrfToken } = useSession();
  const { enabled, checkEnabled } = useMetaIntegration();
  const [items, setItems] = useState<readonly Campaign[] | null>(null);
  const [readError, setReadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [validations, setValidations] = useState<Record<string, Validation>>({});
  const mounted = useRef(true);
  const lock = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const load = useCallback(async () => {
    if (accountId === null) return;
    setReadError(false); setItems(null);
    try {
      const data = await readCollection<Campaign>('/api/v1/business-accounts/' + encodeURIComponent(accountId) + '/campaigns');
      if (data.some((item) => item.businessAccountId !== accountId)) throw new Error('Campaign scope mismatch');
      if (mounted.current) setItems(data);
    } catch { if (mounted.current) { setReadError(true); setItems(null); } }
  }, [accountId]);
  useEffect(() => { void load(); }, [load]);
  const writable = enabled === true && csrfToken !== null && !busy && !readError;
  const endpoint = (campaign: Campaign, action: string) => '/api/v1/campaigns/' + encodeURIComponent(campaign.id) + '/' + action;

  const validate = async (campaign: Campaign) => {
    if (!writable || lock.current || accountId === null) return;
    lock.current = true; setBusy(true); setError(null); setMessage(null);
    try {
      if (!await checkEnabled()) throw new Error('Meta disabled');
      if (!mounted.current) return;
      const result = await apiRequest<{ data: Validation }>(endpoint(campaign, 'validate'), {
        method: 'POST', csrfToken, body: { businessAccountId: accountId },
      });
      if (!validValidation(result.data)) throw new Error('Invalid validation response');
      const summary = { canSchedule: result.data.canSchedule, validation: result.data.validation };
      if (mounted.current) {
        setValidations((current) => ({ ...current, [campaign.id]: summary }));
        setMessage('Validação concluída. Nenhuma mensagem foi enviada.');
      }
    } catch (cause) { if (mounted.current) setError(failureMessage(cause)); }
    finally { lock.current = false; if (mounted.current) setBusy(false); }
  };

  const apply = async (scheduledAt: string | null) => {
    if (!writable || lock.current || pending === null || accountId === null) return;
    if (pending.action === 'schedule' && (!validations[pending.campaign.id]?.canSchedule || scheduledAt === null || Date.parse(scheduledAt) <= Date.now())) return;
    lock.current = true; setBusy(true); setError(null); setMessage(null);
    try {
      if (!await checkEnabled()) throw new Error('Meta disabled');
      if (!mounted.current) return;
      const result = await apiRequest<{ data: Campaign }>(endpoint(pending.campaign, pending.action), {
        method: 'POST', csrfToken,
        body: { businessAccountId: accountId, ...(pending.action === 'schedule' ? { scheduledAt } : {}) },
      });
      if (result.data.id !== pending.campaign.id || result.data.businessAccountId !== accountId) throw new Error('Unexpected campaign response');
      if (mounted.current) {
        setItems((current) => current?.map((item) => item.id === result.data.id ? result.data : item) ?? [result.data]);
        setValidations({}); setPending(null); setMessage('Operação confirmada pela API.');
      }
    } catch (cause) { if (mounted.current) setError(failureMessage(cause)); }
    finally { lock.current = false; if (mounted.current) setBusy(false); }
  };

  return <section><header className="page-heading"><div><h1>Campanhas</h1><p>Crie um rascunho, valide o público e confirme o agendamento separadamente.</p></div></header>
    {accountError || readError ? <p role="alert" className="form-error">Não foi possível consultar as campanhas. Os dados não serão presumidos.</p>
      : accounts !== null && account === null ? <div className="empty-rule">Nenhuma BM conectada. Conecte uma conta antes de criar campanhas.</div>
      : items === null ? <p className="loading-row">Consultando campanhas…</p>
      : items.length === 0 ? <div className="empty-rule">Nenhuma campanha criada.</div>
      : <div className="ledger-list">{items.map((campaign) => {
        const validation = validations[campaign.id];
        const draft = ['draft', 'validating', 'blocked'].includes(campaign.state);
        const terminal = ['completed', 'cancelled', 'failed'].includes(campaign.state);
        return <article className="ledger-row campaign-row" key={campaign.id}>
          <div><h2>{campaign.name}</h2><span className={'status-mark status-' + campaign.state}>{stateLabel[campaign.state] ?? campaign.state}</span></div>
          <div><span className="field-label">Agendamento — São Paulo</span><strong>{campaign.scheduledAt === null ? 'Não agendada' : new Date(campaign.scheduledAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</strong></div>
          <div className="row-actions">
            {draft ? <button type="button" disabled={!writable} onClick={() => void validate(campaign)}>Validar {campaign.name}</button> : null}
            {draft && validation?.canSchedule ? <button type="button" disabled={!writable} onClick={() => { setError(null); setPending({ campaign, action: 'schedule' }); }}>Agendar {campaign.name}</button> : null}
            {campaign.state === 'running' ? <button type="button" disabled={!writable} onClick={() => { setError(null); setPending({ campaign, action: 'pause' }); }}>Pausar {campaign.name}</button> : null}
            {campaign.state === 'paused' ? <button type="button" disabled={!writable} onClick={() => { setError(null); setPending({ campaign, action: 'resume' }); }}>Retomar {campaign.name}</button> : null}
            {!terminal ? <button type="button" className="danger-action" disabled={!writable} onClick={() => { setError(null); setPending({ campaign, action: 'cancel' }); }}>Cancelar {campaign.name}</button> : null}
          </div>
          {validation === undefined ? null : <section className="validation-panel" aria-label={'Validação de ' + campaign.name}>
            <h3>Resultado da validação</h3><dl><div><dt>Elegíveis</dt><dd>{validation.validation.eligible}</dd></div><div><dt>Suprimidos</dt><dd>{validation.validation.suppressed}</dd></div><div><dt>Inválidos</dt><dd>{validation.validation.invalid}</dd></div><div><dt>Duplicados</dt><dd>{validation.validation.duplicate}</dd></div></dl>
            <p>{validation.canSchedule ? 'Pode ser agendada mediante confirmação. A API revalida o público ao agendar.' : 'Agendamento bloqueado. Resolva os impedimentos e valide novamente.'}</p>
            {validation.validation.errors.map((item, index) => <p className="form-error" key={item.code + index}>{validationMessages[item.code] ?? item.code}</p>)}
          </section>}
        </article>;
      })}</div>}
    {account === null ? null : <><button className="list-refresh" type="button" disabled={busy} onClick={() => { setValidations({}); void load(); }}>Atualizar campanhas</button>
      <CampaignComposer accountId={account.id} accountName={account.displayName} csrfToken={csrfToken} disabled={busy || readError} onCreated={(campaign) => {
        setItems((current) => [campaign, ...(current ?? []).filter((item) => item.id !== campaign.id)]);
        setMessage('Rascunho criado. Nenhuma mensagem foi enviada.');
      }}/></>}
    {message === null ? null : <p role="status">{message}</p>}
    {error === null || pending !== null ? null : <p role="alert" className="form-error">{error}</p>}
    {pending === null || account === null ? null : <CampaignConfirmation key={pending.campaign.id + pending.action} pending={pending} accountName={account.displayName}
      eligible={validations[pending.campaign.id]?.validation.eligible ?? null} busy={busy} enabled={enabled === true && csrfToken !== null} error={error}
      onBack={() => { if (!busy) { setPending(null); setError(null); } }} onConfirm={(instant) => void apply(instant)}/>}
  </section>;
};
